import { useRouter } from "next/router";
import { EventsGridOptionsType } from "@/types/ModuleOptionsType";
import { EventCard, EventCardType } from "../../components/EventCard/EventCard";
import { Section } from "../../components/Section/Section";
import { FadeInUp } from "../../components/Animation/ScrollAnimation";
import {
  Grid,
  GridContent,
  GridItem,
  GridDateRangeFilter,
} from "@aleksundshantu/aunds-utils-react/primitives/grid";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { useSublinesVisibility } from "../../hooks/useSublinesOverlinesVisibility";
import { Modul_Termin_Grid } from "../../wp/types/acf-module-types";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import { fetchEvents } from "@/getData/fetchEvents";
import { HeadlineV2 } from "@/components/Headline/HeadlineV2";
import { mockEvent } from "@/ui/mock-data";

export type EventsGridType = Modul_Termin_Grid;

interface EventsGridProps {
  data: EventsGridType;
  eventsGridOptions: EventsGridOptionsType;
}

export type EventsDataType = {
  totalPosts: number;
  elements: EventCardType[];
};

const EventGridModule = ({ data, eventsGridOptions }: EventsGridProps) => {
  const { headline_html_tag, headline, overline, subline } = data.events_grid;

  const {
    section_margin_class,
    section_padding_class,
    background_color_class,
  } = data.layout;

  const {
    box: { show_border_radius, show_box_shadow, show_card_box },
    show_overline_subline,
    grid_options: {
      events_show_range,
      card_events_grid_options: { show_old_events },
    },
  } = eventsGridOptions;

  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline,
  );

  const { locale, defaultLocale } = useRouter();
  const { getLabel } = useGlobalContext();

  {
    /* jscpd:ignore-start */
  }
  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }
  {
    /* jscpd:ignore-end */
  }

  return (
    <Section
      className={`events-grid  ${background_color_class} ${section_margin_class} ${section_padding_class}`}
      disableColumns
      /*ref={mainRef} */
    >
      <FadeInUp>
        <HeadlineV2
          headline_html_tag={headline_html_tag}
          headline={headline}
          overline={showOverline ? overline : ""}
          subline={showSubline ? subline : ""}
          extraClassName="has-text-centered"
          disableAnimation
        />
      </FadeInUp>

      <FadeInUp>
        <Grid
          postsPerPage={100}
          fetchItems={({ filter }) => {
            return fetchEvents({
              locale,
              defaultLocale,
              startDate: filter.dateRangeFilter?.startDate,
              endDate: filter.dateRangeFilter?.endDate,
            });
          }}
          filterOptions={{
            dateRangeFilter: {
              type: events_show_range,
              showOldEvents: show_old_events,
            },
          }}
          labels={{ noItems: getLabel("no_events") }}
          renderLoading={{ numberOfItemsWhileLoading: 9 }}
          enableScroll={false}
        >
          {(events, loading) => {
            return (
              <>
                <GridDateRangeFilter />

                <GridContent gap="2rem">
                  {events.map((element, index) => {
                    return (
                      <GridItem
                        itemWidth={{
                          mobile: "100%",
                          tablet: "50%",
                          desktop: "33.3333%",
                        }}
                        key={index}
                      >
                        <EventCard
                          skeleton={loading}
                          headlineTag={headline_html_tag}
                          element={
                            loading ? mockEvent : (element as EventCardType)
                          }
                          show_border_radius={show_border_radius}
                          show_box_shadow={show_box_shadow}
                          moduleType="events-grid"
                          showBox={show_card_box}
                        />
                      </GridItem>
                    );
                  })}
                </GridContent>
              </>
            );
          }}
        </Grid>
      </FadeInUp>
    </Section>
  );
};

export default EventGridModule;
